import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useActiveFamily } from '../../../context/ActiveFamilyContext';
import { PlanComparisonResult, FeatureComparison } from '../../../services/planComparisonService';
import { DowngradeSelections, DowngradeRecommendations } from '../../../services/downgradeService';

interface Activity {
  id: string;
  name: string;
  icon: string;
  points: number;
  frequency: string;
  active: boolean;
}

interface ActivitySelectionStepProps {
  comparison: PlanComparisonResult;
  recommendations: DowngradeRecommendations | null;
  selections: DowngradeSelections;
  onUpdateSelection: (key: keyof DowngradeSelections, value: any) => void;
}

export default function ActivitySelectionStep({
  comparison,
  recommendations,
  selections,
  onUpdateSelection,
}: ActivitySelectionStepProps) {
  const { activeFamily } = useActiveFamily();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'points' | 'recommended'>('recommended');
  
  // Obtener el límite del cambio
  const activitiesChange = comparison.changes.find(c => c.feature === 'activities');
  const limit = activitiesChange?.newLimit || 3;
  const selectedIds = selections.activeActivityIds || [];
  const selectedCount = selectedIds.length;
  const isValid = selectedCount === limit;
  
  // Cargar actividades
  useEffect(() => {
    loadActivities();
  }, [activeFamily?.id]);
  
  const loadActivities = async () => {
    if (!activeFamily?.id) {
      console.log('❌ No hay activeFamily.id');
      return;
    }
    
    console.log('📥 Cargando actividades para familyId:', activeFamily.id);
    setLoading(true);
    try {
      // Cargar TODAS las actividades, no solo las activas
      const q = query(
        collection(db, 'activities'),
        where('familyId', '==', activeFamily.id)
      );
      const snapshot = await getDocs(q);
      console.log('📥 Actividades encontradas en Firestore:', snapshot.size);
      
      const acts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Activity[];
      
      console.log('✅ Actividades cargadas:', acts.length, acts);
      setActivities(acts);
    } catch (error) {
      console.error('❌ Error cargando actividades:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Filtrar y ordenar actividades
  const filteredActivities = activities
    .filter(activity =>
      activity.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Primero mostrar recomendados
      if (sortBy === 'recommended') {
        const aRec = recommendations?.activityIds.includes(a.id) ? 1 : 0;
        const bRec = recommendations?.activityIds.includes(b.id) ? 1 : 0;
        if (aRec !== bRec) return bRec - aRec;
      }
      
      // Luego por criterio seleccionado
      if (sortBy === 'points') return b.points - a.points;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      
      return 0;
    });
  
  const handleToggle = (activityId: string) => {
    let newSelection: string[];
    
    if (selectedIds.includes(activityId)) {
      // Deseleccionar
      newSelection = selectedIds.filter(id => id !== activityId);
    } else {
      // Seleccionar (si no excede el límite)
      if (selectedCount < limit) {
        newSelection = [...selectedIds, activityId];
      } else {
        // Ya alcanzó el límite, no hacer nada
        return;
      }
    }
    
    onUpdateSelection('activeActivityIds', newSelection);
  };
  
  const handleSelectRecommended = () => {
    if (recommendations?.activityIds) {
      onUpdateSelection('activeActivityIds', recommendations.activityIds.slice(0, limit));
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando actividades...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Selecciona {limit} Actividades
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          El plan gratuito permite {limit} actividades activas. Las demás se pausarán (no se borran).
        </p>
      </div>
      
      {/* Selection counter */}
      <div className={`rounded-xl p-4 border-2 ${
        isValid 
          ? 'bg-green-50 dark:bg-green-900/20 border-green-500' 
          : selectedCount < limit
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
            : 'bg-red-50 dark:bg-red-900/20 border-red-500'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-3xl">
              {isValid ? '✅' : selectedCount < limit ? '📊' : '⚠️'}
            </div>
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">
                {selectedCount} de {limit} seleccionadas
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {isValid 
                  ? '¡Perfecto! Puedes continuar' 
                  : selectedCount < limit
                    ? `Selecciona ${limit - selectedCount} más`
                    : 'Has alcanzado el límite'}
              </div>
            </div>
          </div>
          
          {recommendations && recommendations.activityIds.length >= limit && (
            <button
              onClick={handleSelectRecommended}
              className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              ⭐ Usar Recomendadas
            </button>
          )}
        </div>
      </div>
      
      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            placeholder="🔍 Buscar actividades..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          className="px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="recommended">📌 Recomendadas primero</option>
          <option value="points">⭐ Más puntos</option>
          <option value="name">🔤 Nombre A-Z</option>
        </select>
      </div>
      
      {/* Activities list */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filteredActivities.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No se encontraron actividades' : 'No tienes actividades activas'}
          </div>
        ) : (
          filteredActivities.map(activity => {
            const isSelected = selectedIds.includes(activity.id);
            const isRecommended = recommendations?.activityIds.includes(activity.id);
            const canSelect = selectedCount < limit || isSelected;
            
            return (
              <button
                key={activity.id}
                onClick={() => canSelect && handleToggle(activity.id)}
                disabled={!canSelect}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 shadow-md'
                    : canSelect
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md'
                      : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Checkbox */}
                  <div className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                    isSelected
                      ? 'bg-purple-600 border-purple-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {isSelected && (
                      <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className="text-2xl flex-shrink-0">
                    {activity.icon || '🏃'}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white truncate">
                        {activity.name}
                      </span>
                      {isRecommended && (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                          🔸 RECOMENDADO
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 dark:text-gray-400">
                      <span>⭐ {activity.points} pts</span>
                      <span>•</span>
                      <span>{activity.frequency === 'daily' ? 'Diario' : activity.frequency}</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
      
      {/* Summary */}
      {activities.length > limit && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border-2 border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">ℹ️</span>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <p className="font-medium mb-1">
                ⏸️ {activities.length - limit} actividades se pausarán
              </p>
              <p>
                Las actividades no seleccionadas permanecerán guardadas y podrás reactivarlas al renovar tu plan.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
